import { useEffect, useRef, useState, type ReactNode } from 'react'
import { useLocation } from 'react-router-dom'
import { MoreHorizontal } from 'lucide-react'
import { useNavigate } from '../nav'
import type { BugListItem } from '../../data/projectTypes'
import { isWebhookBlocked } from '../../../netlify/functions/lib/bug-management/statuses'
import StatusBadge from '../StatusBadge'
import BugStatusMenu from './BugStatusMenu'
import { useSendBugToWebhookMutation } from '../../services/projectDataApi'
import useIsMobile from '../../hooks/useIsMobile'
import { bugCategoryLabel } from '../../../netlify/functions/lib/polish-pass-types'
import BugEnvironment from './BugEnvironment'
import { securityImpact } from '../../lib/securityImpact'
import PullRequestCheckStatus from '../PullRequestCheckStatus'
import BugSummaryLine from './BugSummaryLine'
import BugTitleTooltip from './BugTitleTooltip'
import './BugTable.css'

const SEVERITY_TOOLTIPS: Record<string, string> = {
  critical: 'Critical — blocks core functionality',
  high: 'High — major feature is broken or degraded',
  medium: 'Medium — noticeable issue with a workaround',
  low: 'Low — minor cosmetic or edge-case issue',
}

interface BugTableProps {
  bugs: BugListItem[]
  projectId: string
  loading: boolean
  hasWebhook: boolean
  renderDetails?: (bug: BugListItem) => ReactNode
  showSecurityImpact?: boolean
  showPullRequests?: boolean
  /** Drop the Kind column where every row shares one kind (e.g. the Security tab's bugs). */
  showKind?: boolean
  /** Drop the Environment column where it doesn't fit or doesn't inform (previews, security). */
  showEnvironment?: boolean
  /** Adds a checkbox per row (plus select-all) for batch actions. Omit for a read-only table. */
  selection?: BugTableSelection
}

export interface BugTableSelection {
  selectedIds: ReadonlySet<string>
  /** Set every id in `ids` to `selected` (one id for a click, a range for shift-click, the page for select-all). */
  onChange: (ids: string[], selected: boolean) => void
}

// The row checkbox. The click (not change) event carries the shift key, which the table uses to extend
// the selection as a range.
function SelectCheckbox({ bug, checked, onSelect }: {
  bug: BugListItem
  checked: boolean
  onSelect: (shiftKey: boolean) => void
}) {
  return (
    <input
      type="checkbox"
      className="bug-table__checkbox"
      checked={checked}
      onChange={() => { /* handled in onClick */ }}
      onClick={(e) => onSelect(e.shiftKey)}
      aria-label={`Select ${bug.title}`}
      data-no-navigate
      data-testid={`bug-select-${bug.id}`}
      data-analytics-event="bug-row-select"
    />
  )
}

function SelectAllCheckbox({ bugs, selection }: { bugs: BugListItem[]; selection: BugTableSelection }) {
  const ref = useRef<HTMLInputElement>(null)
  const count = bugs.filter(b => selection.selectedIds.has(b.id)).length
  const all = count > 0 && count === bugs.length
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = count > 0 && !all
  }, [count, all])
  return (
    <input
      ref={ref}
      type="checkbox"
      className="bug-table__checkbox"
      checked={all}
      onChange={() => selection.onChange(bugs.map(b => b.id), !all)}
      aria-label={all ? 'Deselect all bugs' : 'Select all bugs'}
      data-testid="bug-select-all"
      data-analytics-event="bug-select-all"
    />
  )
}

// Self-contained "send to webhook" action (own sending state) so both the desktop table cell and the
// mobile card can render it without the parent threading per-bug sending state. Wrapped in
// data-no-navigate so clicking it never triggers the row/card navigation.
function WebhookAction({ bug }: { bug: BugListItem }) {
  const [sendBugToWebhook] = useSendBugToWebhookMutation()
  const [sending, setSending] = useState(false)

  if (bug.sent_to_webhook) return <>Sent</>
  if (isWebhookBlocked(bug.status)) return <>Not Sent</>

  return (
    <button
      type="button"
      className="bug-table__send-webhook-btn"
      disabled={sending}
      data-no-navigate
      onClick={() => {
        setSending(true)
        void sendBugToWebhook(bug.id).finally(() => setSending(false))
      }}
      data-testid={`bug-send-webhook-${bug.id}`}
      data-analytics-event="bug-webhook-send-button"
    >
      {sending ? 'Sending...' : 'Send'}
    </button>
  )
}

function BugRowActions({ bug }: { bug: BugListItem }) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const close = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', close)
    return () => document.removeEventListener('mousedown', close)
  }, [open])

  return (
    <div className="bug-table__row-actions" ref={ref} data-no-navigate>
      <button
        type="button"
        className="bug-table__more-btn"
        aria-label={`More actions for ${bug.title}`}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen(value => !value)}
        data-testid={`bug-more-${bug.id}`}
      >
        <MoreHorizontal size={17} aria-hidden />
      </button>
      {open && (
        <div className="bug-table__more-menu" role="menu" data-testid={`bug-more-menu-${bug.id}`}>
          <span className="bug-table__more-label">Webhook</span>
          <WebhookAction bug={bug} />
        </div>
      )}
    </div>
  )
}

// Order is kept identical to the desktop table's columns so the kind/test-run values match.
function bugKind(bug: BugListItem): string {
  const raw = bug.polish_category ?? (bug.test_run_id ? 'testing' : null)
  return raw ? bugCategoryLabel(raw) : '—'
}

// One summary line per row: the security impact where the table shows it, else the observed behavior.
function bugSummaryText(bug: BugListItem, showSecurityImpact: boolean): string | null | undefined {
  return (showSecurityImpact ? securityImpact(bug.analysis) : null) || bug.actual_behavior
}

function BugSummary({ bug, showSecurityImpact }: { bug: BugListItem; showSecurityImpact: boolean }) {
  const impact = showSecurityImpact ? securityImpact(bug.analysis) : null
  if (impact) return <p className="bug-table__impact">{impact}</p>
  return <BugSummaryLine text={bug.actual_behavior} />
}

function FixPullRequests({ bug }: { bug: BugListItem }) {
  if (!bug.fix_prs?.length) return <>—</>
  return <div className="bug-table__pull-requests">{bug.fix_prs.map(pr => (
    <span key={`${pr.repo_full_name}#${pr.pr_number}`}>
      <a
        href={`https://github.com/${pr.repo_full_name}/pull/${pr.pr_number}`}
        target="_blank" rel="noopener noreferrer" data-analytics-event="bug-fix-pr-link">
        # {pr.pr_number}
      </a>
      <PullRequestCheckStatus projectId={bug.project_id} repo={pr.repo_full_name} pr={pr.pr_number} />
    </span>
  ))}</div>
}

export default function BugTable({ bugs, projectId, loading, hasWebhook, showKind = true, showEnvironment = true, showSecurityImpact = false, showPullRequests = false, renderDetails, selection }: BugTableProps) {
  const navigate = useNavigate()
  const location = useLocation()
  // Render ONE layout, not both-with-one-hidden: display:none left a zero-size copy of every bug
  // title in the DOM, and a text lookup (ours or a screen reader's) resolves to that dead copy first
  // instead of the visible row.
  const isMobile = useIsMobile()
  const selectAnchor = useRef<number | null>(null)

  // Shift-click extends from the last clicked row, the usual list-selection gesture, so a run of bugs can
  // be picked without clicking each one.
  function selectRow(index: number, shiftKey: boolean) {
    if (!selection) return
    const bug = bugs[index]
    if (!bug) return
    const anchor = selectAnchor.current
    const ids = shiftKey && anchor !== null && anchor < bugs.length
      ? bugs.slice(Math.min(anchor, index), Math.max(anchor, index) + 1).map(b => b.id)
      : [bug.id]
    selectAnchor.current = index
    selection.onChange(ids, !selection.selectedIds.has(bug.id))
  }

  if (loading) {
    return (
      <div className="bug-table-loading" role="status" aria-label="Loading bugs" data-testid="bug-list-loading">
        <span className="bug-table-loading__header" />
        {Array.from({ length: 8 }).map((_, index) => (
          <span key={index} className="bug-table-loading__row" />
        ))}
      </div>
    )
  }

  if (bugs.length === 0) {
    return <div className="bug-table-empty" data-testid="bug-list-empty">No bugs found.</div>
  }

  function openBug(e: React.MouseEvent, bugId: string) {
    if ((e.target as HTMLElement).closest('[data-no-navigate]')) return
    navigate(`/projects/${projectId}/bugs/${bugId}`, {
      state: {
        bugListLocation: {
          pathname: location.pathname,
          search: location.search,
          hash: location.hash,
        },
      },
    })
  }

  // Mobile (≤768px): each bug is its own vertically-stacked card, in place of the desktop table.
  if (isMobile) {
    return (
      <div className="bug-cards" data-testid="bug-cards">
        {selection && (
          <label className="bug-cards__select-all" data-no-navigate>
            <SelectAllCheckbox bugs={bugs} selection={selection} />
            Select all
          </label>
        )}
        {bugs.map((bug, index) => (
          <div
            key={bug.id}
            className={`bug-card ${selection?.selectedIds.has(bug.id) ? 'bug-card--selected' : ''}`}
            onClick={(e) => openBug(e, bug.id)}
            data-testid={`bug-card-${bug.id}`}
            data-analytics-event="bug-row-open"
          >
            {/* The bug's title leads the card, the way it leads its row on desktop; the kebab is the
                only thing that competes with it, and only where a webhook makes it useful. */}
            <div className="bug-card__head">
              {selection && <SelectCheckbox bug={bug} checked={selection.selectedIds.has(bug.id)} onSelect={shift => selectRow(index, shift)} />}
              <div className="bug-card__title">{bug.title}</div>
              {hasWebhook && <BugRowActions bug={bug} />}
            </div>
            <BugSummary bug={bug} showSecurityImpact={showSecurityImpact} />
            {/* Every remaining value is labelled, in the desktop table's column order, so a card and a
                row say the same things in the same sequence. */}
            <dl className="bug-card__facts">
              <dt>Severity</dt>
              <dd data-testid="severity-tooltip" title={SEVERITY_TOOLTIPS[bug.severity] ?? bug.severity}>
                <StatusBadge status={bug.severity} variant="severity" />
              </dd>
              <dt>Status</dt>
              <dd data-no-navigate><BugStatusMenu bug={bug} /></dd>
              {showKind && <>
                <dt>Kind</dt>
                <dd className="bug-card__fact-value--cap">{bugKind(bug)}</dd>
              </>}
              {showPullRequests && <>
                <dt>PR</dt>
                <dd data-no-navigate><FixPullRequests bug={bug} /></dd>
              </>}
              {showEnvironment && <>
                <dt>Environment</dt>
                <dd data-no-navigate><BugEnvironment bug={bug} /></dd>
              </>}
            </dl>
            {renderDetails && <div data-no-navigate>{renderDetails(bug)}</div>}
          </div>
        ))}
      </div>
    )
  }

  return (
    <table className="bug-table" data-testid="bug-table">
      <thead>
        <tr>
          {selection && <th className="bug-table__col-select"><SelectAllCheckbox bugs={bugs} selection={selection} /></th>}
          <th>Severity</th>
          <th className="bug-table__col-title">Title</th>
          <th>Status</th>
          {showKind && <th className="bug-table__col-kind">Kind</th>}
          {showPullRequests && <th>PR</th>}
          {showEnvironment && <th className="bug-table__col-environment">Environment</th>}
          {hasWebhook && <th className="bug-table__col-actions"><span className="sr-only">Actions</span></th>}
        </tr>
      </thead>
      <tbody>
        {bugs.map((bug, index) => (
          <tr
            key={bug.id}
            className={`bug-table__row ${selection?.selectedIds.has(bug.id) ? 'bug-table__row--selected' : ''}`}
            onClick={(e) => openBug(e, bug.id)}
            data-testid={`bug-row-${bug.id}`}
            data-analytics-event="bug-row-open"
            aria-selected={selection ? selection.selectedIds.has(bug.id) : undefined}
          >
            {selection && (
              <td className="bug-table__col-select" data-no-navigate>
                <SelectCheckbox bug={bug} checked={selection.selectedIds.has(bug.id)} onSelect={shift => selectRow(index, shift)} />
              </td>
            )}
            <td>
              <span className="bug-table__tooltip-wrapper" data-testid="severity-tooltip" title={SEVERITY_TOOLTIPS[bug.severity] ?? bug.severity}>
                <StatusBadge status={bug.severity} variant="severity" />
              </span>
            </td>
            <td className="bug-table__title">
              <BugTitleTooltip className="bug-table__title-text" title={bug.title} summary={bugSummaryText(bug, showSecurityImpact)}>
                {bug.title}
                <BugSummary bug={bug} showSecurityImpact={showSecurityImpact} />
              </BugTitleTooltip>
              {renderDetails && <div data-no-navigate>{renderDetails(bug)}</div>}
            </td>
            <td data-no-navigate>
              <BugStatusMenu bug={bug} />
            </td>
            {showKind && <td className="bug-table__kind bug-table__col-kind">{bugKind(bug)}</td>}
            {showPullRequests && <td data-no-navigate><FixPullRequests bug={bug} /></td>}
            {showEnvironment && <td className="bug-table__col-environment" data-no-navigate><BugEnvironment bug={bug} /></td>}
            {hasWebhook && <td className="bug-table__col-actions" data-no-navigate><BugRowActions bug={bug} /></td>}
          </tr>
        ))}
      </tbody>
    </table>
  )
}
